/**
 * Bilan Store - Zustand State Management
 *
 * Manages accounting statement (bilan comptable) state including list,
 * creation, update, deletion, and comparison.
 */

import { create } from 'zustand';
import {
  AccountingStatement,
  AccountingStatementSummary,
  AccountingStatementComparison,
  AccountingStatementFilters,
  AccountingStatementLimitResponse,
  CreateAccountingStatementRequest,
  UpdateAccountingStatementRequest,
} from '@budgira/shared';
import { bilanService } from '../services/bilan.service';
import i18n from '../i18n';

interface BilanState {
  // List state
  statements: AccountingStatementSummary[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;

  // Detail state
  currentStatement: AccountingStatement | null;
  isLoadingDetail: boolean;

  // Comparison state
  comparison: AccountingStatementComparison | null;
  isLoadingComparison: boolean;

  // Limit state
  limit: AccountingStatementLimitResponse | null;

  // Form state
  isSubmitting: boolean;

  // Filters and pagination
  filters: AccountingStatementFilters;
  page: number;
  pageSize: number;

  // Actions
  fetchStatements: () => Promise<void>;
  fetchStatement: (id: string) => Promise<void>;
  createStatement: (
    request: CreateAccountingStatementRequest
  ) => Promise<AccountingStatement | null>;
  updateStatement: (
    id: string,
    request: UpdateAccountingStatementRequest
  ) => Promise<AccountingStatement | null>;
  deleteStatement: (id: string) => Promise<boolean>;
  recalculateStatement: (id: string) => Promise<AccountingStatement | null>;
  compareStatements: (id1: string, id2: string) => Promise<void>;
  checkLimit: () => Promise<void>;
  exportPdf: (id: string) => Promise<Blob | null>;
  exportComparisonPdf: (id1: string, id2: string) => Promise<Blob | null>;
  setFilters: (filters: AccountingStatementFilters) => void;
  setPage: (page: number) => void;
  clearCurrentStatement: () => void;
  clearComparison: () => void;
  clearError: () => void;
  resetStore: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeBilanError(err: unknown): string {
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    const axiosError = err as {
      response?: { data?: { error?: { code?: string; message?: string } } };
    };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    STATEMENT_NOT_FOUND: 'bilans.errors.notFound',
    UNAUTHORIZED: 'bilans.errors.unauthorized',
    VALIDATION_ERROR: 'bilans.errors.validation',
    LIMIT_EXCEEDED: 'bilans.errors.limitExceeded',
    INVALID_DATE_RANGE: 'bilans.errors.invalidDateRange',
    NO_ACCOUNTS_SELECTED: 'bilans.errors.noAccountsSelected',
    ACCOUNT_NOT_FOUND: 'bilans.errors.accountNotFound',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  if (err instanceof Error) {
    return err.message;
  }

  return errorMessage || String(i18n.t('bilans.errors.generic'));
}

export const useBilanStore = create<BilanState>((set, get) => ({
  statements: [],
  totalCount: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
  currentStatement: null,
  isLoadingDetail: false,
  comparison: null,
  isLoadingComparison: false,
  limit: null,
  isSubmitting: false,
  filters: {},
  page: 1,
  pageSize: 10,

  fetchStatements: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const { filters, page, pageSize } = get();
      const result = await bilanService.list(filters, page, pageSize);
      set({
        statements: result.items,
        totalCount: result.total,
        totalPages: result.totalPages,
        isLoading: false,
      });
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isLoading: false });
    }
  },

  fetchStatement: async (id: string): Promise<void> => {
    set({ isLoadingDetail: true, error: null });
    try {
      const statement = await bilanService.getById(id);
      set({ currentStatement: statement, isLoadingDetail: false });
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isLoadingDetail: false });
    }
  },

  createStatement: async (
    request: CreateAccountingStatementRequest
  ): Promise<AccountingStatement | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const created = await bilanService.create(request);
      // Refresh the list
      await get().fetchStatements();
      // Refresh limit
      await get().checkLimit();
      set({ isSubmitting: false });
      return created;
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  updateStatement: async (
    id: string,
    request: UpdateAccountingStatementRequest
  ): Promise<AccountingStatement | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const updated = await bilanService.update(id, request);
      // Refresh the list
      await get().fetchStatements();
      // Update current statement if it's the one being edited
      if (get().currentStatement?.id === id) {
        set({ currentStatement: updated });
      }
      set({ isSubmitting: false });
      return updated;
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  deleteStatement: async (id: string): Promise<boolean> => {
    set({ isSubmitting: true, error: null });
    try {
      await bilanService.delete(id);
      // Refresh the list
      await get().fetchStatements();
      // Refresh limit
      await get().checkLimit();
      // Clear current statement if it's the one being deleted
      if (get().currentStatement?.id === id) {
        set({ currentStatement: null });
      }
      set({ isSubmitting: false });
      return true;
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isSubmitting: false });
      return false;
    }
  },

  recalculateStatement: async (id: string): Promise<AccountingStatement | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const updated = await bilanService.recalculate(id);
      // Update current statement
      set({ currentStatement: updated, isSubmitting: false });
      // Refresh the list
      await get().fetchStatements();
      return updated;
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  compareStatements: async (id1: string, id2: string): Promise<void> => {
    set({ isLoadingComparison: true, error: null });
    try {
      const comparison = await bilanService.compare(id1, id2);
      set({ comparison, isLoadingComparison: false });
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message, isLoadingComparison: false });
    }
  },

  checkLimit: async (): Promise<void> => {
    try {
      const limit = await bilanService.checkLimit();
      set({ limit });
    } catch {
      // Silently fail - limit check is not critical
    }
  },

  exportPdf: async (id: string): Promise<Blob | null> => {
    try {
      return await bilanService.exportPdf(id);
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message });
      return null;
    }
  },

  exportComparisonPdf: async (id1: string, id2: string): Promise<Blob | null> => {
    try {
      return await bilanService.exportComparisonPdf(id1, id2);
    } catch (err) {
      const message = localizeBilanError(err);
      set({ error: message });
      return null;
    }
  },

  setFilters: (filters: AccountingStatementFilters): void => {
    set({ filters, page: 1 });
  },

  setPage: (page: number): void => {
    set({ page });
  },

  clearCurrentStatement: (): void => {
    set({ currentStatement: null });
  },

  clearComparison: (): void => {
    set({ comparison: null });
  },

  clearError: (): void => {
    set({ error: null });
  },

  resetStore: (): void => {
    set({
      statements: [],
      totalCount: 0,
      totalPages: 0,
      isLoading: false,
      error: null,
      currentStatement: null,
      isLoadingDetail: false,
      comparison: null,
      isLoadingComparison: false,
      limit: null,
      isSubmitting: false,
      filters: {},
      page: 1,
      pageSize: 10,
    });
  },
}));

export default useBilanStore;
